import { useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePrintConfigImages } from '@/hooks/usePrintConfigImages';

// Per-tenant figures for "Configuración de Impresión" (1-8). Until a figure is
// uploaded the generic (brand-less) one is shown.
export function PrintConfigImagesCard() {
  const { images, reload } = usePrintConfigImages();
  const inputRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const auth = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const pick = (figure: number) => {
    setTarget(figure);
    setError(null);
    inputRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || target == null) return;
    if (!/^image\/(jpeg|jpg|png|gif)$/.test(file.type)) { setError('Solo se permiten imágenes JPEG, PNG o GIF'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('La imagen debe pesar menos de 2MB'); return; }
    setBusy(target);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await fetch(`/api/print-configs/${target}`, { method: 'PUT', headers: auth, body: form });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Error al subir la imagen');
      reload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const reset = async (figure: number) => {
    setBusy(figure);
    setError(null);
    try {
      const res = await fetch(`/api/print-configs/${figure}`, { method: 'DELETE', headers: auth });
      if (!res.ok) throw new Error('Error al restablecer la imagen');
      reload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Figuras de Configuración de Impresión</CardTitle>
        <CardDescription>
          Estas 8 figuras definen el sentido de impresión en la OP. Sube las de tu empresa (con tu logotipo);
          mientras no subas una, se usa la figura genérica.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={onFile} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map(img => (
            <div key={img.figureNumber} className="border rounded-lg p-2 space-y-2">
              <img src={img.imageUrl} alt={`Figura ${img.figureNumber}`} className="w-full h-auto" />
              <p className="text-xs text-center text-muted-foreground">
                Figura {img.figureNumber} · {img.custom ? 'Personalizada' : 'Genérica'}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" disabled={busy === img.figureNumber} onClick={() => pick(img.figureNumber)}>
                  Subir
                </Button>
                {img.custom && (
                  <Button size="sm" variant="ghost" disabled={busy === img.figureNumber} onClick={() => reset(img.figureNumber)}>
                    Quitar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
